import { $generateHtmlFromNodes } from "@lexical/html";
import { $isLinkNode } from "@lexical/link";
import { $createTextNode, $getRoot, $insertNodes, $isElementNode, type LexicalNode } from "lexical";
import { channelToFormat } from "../../../../../utils/channel-to-format";
import type { ChannelName } from "../../../../channels/types";
import { $isBroadcastNode } from "../../../../message/editor/nodes/broadcast-node";
import { $isChannelNode } from "../../../../message/editor/nodes/channel/channel-node";
import { $isVideoNode } from "../../../../message/editor/nodes/video-node";
import type { SerialisedLexicalEditorDesign } from "../../../../message/editor/types/serialised-editor-design";
import { generateNodesFromHtml } from "../../../../message/editor/utils/generate-nodes-from-html";
import { createConfiguredEditor } from "../../../../message/editor/utils/headless-editor";
import type { ChatChannelRecipient } from "../../../../message/editor/utils/use-chat-channel-recipients";

function unsupportedVideoNode(node: LexicalNode, slackVideoEnabled: boolean): boolean {
  // Cannot sent uploaded videos unless the Slack app has been updated
  return $isVideoNode(node) && !slackVideoEnabled;
}

export function convertLexicalDesign(
  from: NonNullable<ChannelName>,
  to: NonNullable<ChannelName>,
  design: SerialisedLexicalEditorDesign | null,
  slackVideoEnabled: boolean = false,
  slackChannelRecipients: ChatChannelRecipient[] = [],
  blackListedNodeMatchers: ((node: LexicalNode) => boolean)[] = [],
): SerialisedLexicalEditorDesign {
  const oldEditor = createConfiguredEditor({ format: channelToFormat(from) });

  const state = oldEditor.parseEditorState(JSON.stringify(design));
  oldEditor.setEditorState(state);

  let htmlString = "";
  oldEditor.update(
    () => {
      const stripIllegalNodes = (node: LexicalNode): LexicalNode | null => {
        // 1. Check against custom blacklisted matchers
        if (blackListedNodeMatchers.some((matcher) => matcher(node))) {
          node.remove();
          return null;
        }

        // 2. Existing transformation logic
        if (to === "twilio" && $isLinkNode(node)) {
          return $createTextNode(node.getURL());
        }

        if (from === "slack" && $isBroadcastNode(node)) {
          return $createTextNode(node.getDisplay());
        }

        if (from === "slack" && $isChannelNode(node)) {
          if (slackChannelRecipients.length === 0) {
            return $createTextNode(`#${node.getChannelId()}`);
          }

          const channelName = slackChannelRecipients.find(
            ({ channel_id }) => node.getChannelId() === channel_id,
          )?.display;
          const display = channelName ? `#${channelName}` : "⚠️ Unknown Channel";
          return $createTextNode(display);
        }

        // 3. Recursive step for ElementNodes
        if ($isElementNode(node)) {
          const childNodes = node.getChildren();
          // Filter out nulls returned by blacklisted removals
          const preparedChildNodes = childNodes
            .map(stripIllegalNodes)
            .filter((n): n is LexicalNode => n !== null);

          node.clear(); // Cleaner than manual remove loop
          node.append(...preparedChildNodes);
        }

        return node;
      };

      const topLevelNodes = $getRoot().getChildren();
      const processedNodes = topLevelNodes
        .map(stripIllegalNodes)
        .filter((n): n is LexicalNode => n !== null);

      $getRoot().clear();
      $getRoot().append(...processedNodes);

      htmlString = $generateHtmlFromNodes(oldEditor, null);
    },
    { discrete: true },
  );

  const newEditor = createConfiguredEditor({ format: channelToFormat(to) });

  newEditor.update(
    () => {
      const nodes = generateNodesFromHtml(newEditor, htmlString, to);

      // Filter final nodes against video constraints AND the blacklist
      const filteredNodes = nodes.filter((node) => {
        const isUnsupportedVideo = to === "slack" && unsupportedVideoNode(node, slackVideoEnabled);
        const isBlacklisted = blackListedNodeMatchers.some((m) => m(node));
        return !isUnsupportedVideo && !isBlacklisted;
      });

      $insertNodes(filteredNodes);
    },
    { discrete: true },
  );

  return newEditor.getEditorState().toJSON();
}
